import { useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";

const JANE_URL = "https://hayahealingpsychotherapy.janeapp.com/";

const NAV_ITEMS = [
  ["About", "/about", [
    ["Meet Momna", "/about"],
    ["Fees & Insurance", "/fees"],
  ]],
  ["Our Approach", "/#approach", [
    ["Cognitive Behavioural Therapy (CBT)", "/approach/cbt"],
    ["Cognitive Processing Therapy (CPT)", "/approach/cpt"],
    ["Dialectical Behaviour Therapy (DBT)", "/approach/dbt"],
    ["Acceptance & Commitment Therapy (ACT)", "/approach/act"],
    ["Gottman Method Couples Therapy", "/approach/gottman"],
    ["Humanistic Therapy", "/approach/humanistic"],
    ["Psychodynamic Therapy", "/approach/psychodynamic"],
    ["Trauma-Informed Care", "/approach/trauma-informed"],
    ["Relational Therapy", "/approach/relational"],
    ["Culturally Responsive Therapy", "/cultural"],
    ["Play Therapy", "/approach/play-therapy"],
  ]],
  ["Psychotherapy Services", "/#focus", [
    ["Individual Psychotherapy", "/concern/individual-therapy"],
    ["Child Psychotherapy", "/concern/child-therapy"],
    ["Teen Psychotherapy", "/concern/teen-therapy"],
    ["Family Therapy", "/concern/family-counselling"],
    ["Couples & Marriage Counselling", "/couples"],
  ]],
  ["Areas of Clinical Focus", "/#services", [
    ["Psychotherapy for Anxiety", "/concern/anxiety"],
    ["Psychotherapy for Depression", "/concern/depression"],
    ["Trauma Psychotherapy", "/concern/trauma-therapy"],
    ["Psychotherapy for ADHD", "/concern/adhd"],
    ["Emotional Regulation", "/concern/emotional-regulation"],
    ["Grief Counselling", "/concern/grief-counselling"],
    ["Relationships & Dating", "/concern/relationships-dating"],
    ["Premarital Counselling", "/concern/premarital-counselling"],
    ["South Asian Women's Psychotherapy", "/concern/south-asian-womens-therapy"],
    ["Faith-Based Psychotherapy", "/concern/faith-based-therapy"],
    ["View all concerns →", "/#services"],
  ]],
  ["Resources", "/resources", [
    ["Psychoeducation", "/resources#psychoeducation"],
    ["Crisis Support — Hotlines & Help Lines", "/crisis-support"],
  ]],
  ["Contact", "/contact", [
    ["Send a Message", "/contact"],
    ["Book Initial Consultation", "https://hayahealingpsychotherapy.janeapp.com/"],
    ["momna@hayahealingpsychotherapy.com", "mailto:momna@hayahealingpsychotherapy.com"],
    ["(365) 617-2135", "tel:+13656172135"],
  ]],
];

const MobileMenu = ({ open, onClose }) => open && (
  <div data-testid="mobile-menu" className="lg:hidden fixed inset-x-0 top-[88px] bottom-0 z-30 bg-[#fcfcf9] border-b border-[#2c1f20]/10 shadow-[0_20px_50px_-20px_rgba(44,31,32,0.25)] overflow-y-auto">
    <div className="px-6 py-5">
      <a href="/" onClick={onClose} className="block py-2 font-serif-display text-lg">Home</a>
      {NAV_ITEMS.map(([l, h, dd]) => (
        <div key={l} className="py-2 border-t border-[#2c1f20]/10">
          <a href={h} onClick={onClose} data-testid={`mobile-nav-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="block font-serif-display text-lg">{l}</a>
          <div className="pl-4 mt-1 border-l border-[#2c1f20]/10">
            {dd.map(([dl, dh]) => (
              <a key={dl} href={dh} {...(dh.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
                onClick={onClose}
                className="block py-1.5 text-sm text-[#2c1f20]/70 hover:text-[#6e4550] transition-colors">
                {dl}
              </a>
            ))}
          </div>
        </div>
      ))}
      <a href={JANE_URL} target="_blank" rel="noreferrer" onClick={onClose}
        className="pill-btn pill-solid text-sm mt-4 w-full justify-center">Book Initial Consultation</a>
    </div>
  </div>
);

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
  <>
  <header className="sticky top-0 z-40 flex items-center justify-between px-6 md:px-14 py-4 bg-[#fcfcf9]/85 backdrop-blur border-b border-[#2c1f20]/10">
    <a href="/" data-testid="nav-home-link" className="flex items-center gap-3">
      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden bg-white border border-[#2c1f20]/10 shadow-[0_10px_30px_-12px_rgba(44,31,32,0.25)]">
        <img src="/haya-mark.png" alt="Haya Healing Psychotherapy" data-testid="nav-logo" className="w-full h-full object-contain p-2" />
      </div>
      <div className="leading-tight">
        <p className="font-serif-display text-base md:text-lg tracking-[0.12em] text-[#2c1f20]">HAYA HEALING</p>
        <p className="text-[10px] tracking-[0.32em] uppercase text-[#8a5a63]">Psychotherapy</p>
      </div>
    </a>
    <nav className="hidden lg:flex items-center gap-5 xl:gap-7 text-sm text-[#2c1f20]/80">
      <a href="/" className="hover:text-[#2c1f20] transition-colors">Home</a>
      {NAV_ITEMS.map(([l, h, dd]) => (
        <div key={l} className="relative group">
          <a href={h} data-testid={`nav-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-1 whitespace-nowrap hover:text-[#2c1f20] transition-colors py-2">
            {l} <ChevronDown size={12} className="text-[#8a5a63] group-hover:rotate-180 transition-transform" />
          </a>
          <div className="absolute left-1/2 -translate-x-1/2 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
            <div className="bg-white border border-[#2c1f20]/10 rounded-md shadow-[0_20px_50px_-20px_rgba(44,31,32,0.25)] py-2 w-72">
              {dd.map(([dl, dh]) => (
                <a key={dl} href={dh} {...(dh.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
                  className="block px-5 py-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] hover:bg-[#f1ece4]/60 transition-colors">
                  {dl}
                </a>
              ))}
            </div>
          </div>
        </div>
      ))}
    </nav>
    <div className="flex items-center gap-3">
      <span className="hidden sm:block">
        <a data-testid="nav-book-button" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn text-sm bg-[#2c1f20] text-[#fcfcf9] hover:bg-[#6e4550]">Book Initial Consultation</a>
      </span>
      <button data-testid="mobile-menu-button" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu"
        className="lg:hidden w-11 h-11 rounded-full border border-[#2c1f20]/15 flex items-center justify-center text-[#2c1f20] hover:bg-[#f1ece4] transition-colors">
        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
      </button>
    </div>
  </header>
  <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
  </>
  );
}
